import React from "react";

/**
 * CommonStepIndicator - A premium animated and flexible step indicator.
 * 
 * Props:
 *   steps         {array}   Array of strings (labels) or objects { id, label }
 *   currentStep   {number}  The current active step number (1-indexed)
 *   className     {string}  Extra classes for the container
 */
const CommonStepIndicator = ({ 
  steps = [], 
  currentStep = 1, 
  className = ""
}) => {
  if (!steps || steps.length === 0) return null;

  // Normalize steps to objects
  const normalizedSteps = steps.map((s, i) => (typeof s === "string" ? { id: i + 1, label: s } : s));
  const totalSteps = normalizedSteps.length;

  // Calculate percentage progress between circle centers
  const progressPercent = totalSteps > 1 
    ? ((currentStep - 1) / (totalSteps - 1)) * 100 
    : 0;

  return (
    <div className={`d-flex align-items-start justify-content-between mb-5 mt-4 w-100 position-relative ${className}`} style={{ maxWidth: "800px", margin: "0 auto" }}>
      {/* Background Line Container - constrained between circle centers */}
      <div 
        className="position-absolute w-100" 
        style={{ 
          top: "11px", 
          left: 0, 
          height: "2px", 
          zIndex: 0, 
          padding: "0 60px" // This ensures the line starts and ends at the 50% point of the first and last step containers (120px / 2)
        }}
      >
        <div style={{ position: "relative", width: "100%", height: "100%", background: "#eee" }}>
          <div 
            style={{ 
              position: "absolute", 
              left: 0, 
              top: 0, 
              height: "100%", 
              background: "var(--primary)", 
              width: `${progressPercent}%`,
              transition: "width 0.3s ease" 
            }} 
          />
        </div>
      </div>

      {/* Render Steps */}
      {normalizedSteps.map((step, index) => {
        const stepNum = index + 1;
        const isCurrentOrPast = stepNum <= currentStep;

        return (
          <div
            key={index}
            className="d-flex flex-column align-items-center position-relative"
            style={{ zIndex: 1, width: "120px" }}
          >
            <div
              className="rounded-circle d-flex align-items-center justify-content-center fw-bold fs-12 mb-2"
              style={{
                width: "24px",
                height: "24px",
                backgroundColor: isCurrentOrPast ? "var(--primary)" : "#f8f9fa",
                color: isCurrentOrPast ? "#fff" : "#ccc",
                border: isCurrentOrPast ? "none" : "1px solid #ccc",
                transition: "all 0.3s ease",
              }}
            >
              {stepNum}
            </div>
            <span
              className="text-center"
              style={{
                fontSize: "10px",
                color: isCurrentOrPast ? "var(--primary)" : "#888",
                fontWeight: isCurrentOrPast ? "bold" : "normal",
                whiteSpace: "nowrap",
                transition: "color 0.3s ease",
              }}
            >
              {step.label}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export default CommonStepIndicator;
